import { getTurnTime, Play, GameModeMeta, PLAYER_COLOR, MoveAPI } from '@whitesharx/starfall-frontend-core';

import { ProcessedAnalyzedMove } from './types';

export const getTimers = ({
  game,
  move,
  playerId,
  meta,
}: {
  game?: Play;
  move?: ProcessedAnalyzedMove | null;
  playerId?: string;
  meta?: GameModeMeta[];
}) => {
  if (!game || !playerId || !meta) {
    return { playerLeftTime: null, enemyLeftTime: null };
  }

  const metaByContext = meta.find((context) => context.name === game.contextType)!;
  const isWhitePlayer = playerId === game.whitePlayerId;

  if (move) {
    const moveIndex = game.moves.findIndex((gameMove) => gameMove.date === move.date);
    const prevMoves = [...game.moves].slice(0, moveIndex);

    const whiteTurnTime = getTurnTime({
      moves: prevMoves,
      turnTime: game.turnTime,
      firstMoveTime: game.firstMoveTime,
      playerLeftTime: metaByContext.turnTime,
      playerColor: PLAYER_COLOR.white,
    });

    const blackTurnTime = getTurnTime({
      moves: prevMoves,
      turnTime: game.turnTime,
      firstMoveTime: game.firstMoveTime,
      playerLeftTime: metaByContext.turnTime,
      playerColor: PLAYER_COLOR.black,
    });

    const spentTime = metaByContext.turnTimeIsGlobal
      ? spentTimePerMoves({ game, moves: [...prevMoves, move], additionalTurnTime: metaByContext.additionalTurnTime })
      : spentTimePerLastMove({ game, moves: [...prevMoves, move] });

    const { whiteLeftTime, blackLeftTime } = {
      whiteLeftTime: whiteTurnTime! - spentTime.white,
      blackLeftTime: blackTurnTime! - spentTime.black,
    };

    const { playerLeftTime, enemyLeftTime } = {
      playerLeftTime: isWhitePlayer ? whiteLeftTime : blackLeftTime,
      enemyLeftTime: isWhitePlayer ? blackLeftTime : whiteLeftTime,
    };

    return {
      playerLeftTime: metaByContext.turnTimeIsGlobal || move.playerId === playerId ? playerLeftTime : null,
      enemyLeftTime: metaByContext.turnTimeIsGlobal || move.playerId !== playerId ? enemyLeftTime : null,
    };
  } else {
    //timers in initial values
    const firstMoveTime = game.firstMoveTime || game.turnTime;

    const { whiteLeftTime, blackLeftTime } = {
      whiteLeftTime: firstMoveTime,
      blackLeftTime: metaByContext.turnTimeIsGlobal ? firstMoveTime : null,
    };

    return {
      playerLeftTime: isWhitePlayer ? whiteLeftTime : blackLeftTime,
      enemyLeftTime: isWhitePlayer ? blackLeftTime : whiteLeftTime,
    };
  }
};

const spentTimePerMove = ({ prevMove, move, game }: { prevMove?: MoveAPI; move: MoveAPI; game: Play }) => {
  const prevMoveDate = prevMove?.date || game.createdAt;
  return new Date(move.date).getTime() - new Date(prevMoveDate).getTime();
};

const spentTimePerMoves = ({
  game,
  moves,
  additionalTurnTime,
}: {
  game: Play;
  moves: MoveAPI[];
  additionalTurnTime?: number;
}) =>
  moves.reduce(
    (acc, current, index, array) => {
      const spentTime = spentTimePerMove({ prevMove: array[index - 1], move: current, game });
      const isLastMove = index === array.length - 1;
      const additionalTime = (!isLastMove && additionalTurnTime) || 0;

      return current.playerId === game.whitePlayerId
        ? { black: acc.black, white: acc.white + spentTime - additionalTime }
        : { white: acc.white, black: acc.black + spentTime - additionalTime };
    },
    { white: 0, black: 0 }
  );

const spentTimePerLastMove = ({ game, moves }: { game: Play; moves: MoveAPI[] }) => {
  const prevMove = moves.at(-2);
  const move = moves.at(-1)!;
  const spentTime = spentTimePerMove({ prevMove, move, game });

  return move.playerId === game.whitePlayerId ? { black: 0, white: spentTime } : { white: 0, black: spentTime };
};
